// libs.
import dayjs from 'dayjs';

// APIs.
import * as api from '@/api';
import {
  AbsenceProfile,
  AbsenceProfileResourceClient,
  CalendarClient,
  CasesviewClient,
  GetScheduleRowsResponseRequest,
} from '@/api';

import { AbsenceType, getAbsenceTypesFromForm } from '@/views/modules/absences/config/constants';
import { parseRows, getValidationIds, loadTasksWithVariables } from '@/views/modules/absences/utils/scheduleRowUtils';
import {
  ChartConfig,
  EmployeeScheduleTaskVariables,
  ValidationIds,
} from '@/views/modules/absences/utils/types';
import { defineModule } from './module-utils';

const absenceProfilesApi = new AbsenceProfileResourceClient();
const casesviewClient = new CasesviewClient();
const formClient = new api.FormClient();
const calendarClient = new CalendarClient();

const initialState = {
  absenceProfiles: [] as AbsenceProfile[],
  countries: [] as { id: string; name: string; }[],
  employeeSchedules: [] as EmployeeScheduleTaskVariables[],
  departmentSchedules: [] as EmployeeScheduleTaskVariables[],
  managerApprovalSchedules: [] as EmployeeScheduleTaskVariables[],
  schedulerOptions: null as EmployeeScheduleTaskVariables,
  absenceTypes: [] as AbsenceType[],
  caseView: { meta: {} } as api.CaseConfiguration,
  schedulerRows: [] as any[],
  employeeRowsToClear: [] as string[],
  employeeValidationIds: null as ValidationIds,
  departmentValidationIds: null as ValidationIds,
};

export type AbsenceState = typeof initialState;

// const vacationScheduleCaseId = 'graph-request-absences';
const absencesCaseId = 'chart-absences';

const absenceModule = defineModule({
  state: initialState,

  getters: {
    absenceProfileCountries(state) {
      const uniqueCountryCodes = new Set<string>();
      state.absenceProfiles.forEach((profile) => {
        uniqueCountryCodes.add(profile.country);
      });
      return [...uniqueCountryCodes].map((code) => ({ id: code, name: code }));
    },
    defaultUserCountry(state) {
      for (const profile of state.absenceProfiles) {
        if (profile.isDefault) {
          return profile.country ?? 'EE';
        }
      }
      return 'EE';
    },
    currentUserCountry(state, getters, _rootState, rootGetters): string {
      const userAbsenceProfile = rootGetters.user?.absenceProfile;
      for (const profile of state.absenceProfiles) {
        if (userAbsenceProfile && userAbsenceProfile === profile.name) {
          return profile?.country ?? getters.defaultUserCountry;
        }
      }
      return getters.defaultUserCountry;
    },
    schedulerOptions: (state) => state.schedulerOptions,
    absenceTypes: (state) => state.absenceTypes,
    absenceChartConfig: (state) => state.caseView.meta.chartConfig as ChartConfig,
    absenceChartCaseView: (state) => state.caseView,
    departmentFilterLocked(state, _getters, _rootState, rootGetters) {
      const { caseView } = state;
      if (!caseView || !caseView.meta || !caseView.meta.departmentFilterLockedGroups) {
        return false;
      }
      const restrictedGroups = caseView.meta.departmentFilterLockedGroups as string[];
      const userGroups = rootGetters.user.groups as string[];
      if (restrictedGroups.length === 0 || !userGroups || userGroups.length === 0) {
        return false;
      }
      const groupIntersect = userGroups.filter((userGroup) => restrictedGroups.includes(userGroup));
      return groupIntersect && groupIntersect.length > 0;
    },
    isSaintGobain(_state, _getters, _rootState, rootGetters) {
      const { tenantId } = rootGetters.user;
      return ['saint-gobain', 'saint-gobain-eesti'].includes(tenantId);
    },
    absenceProfiles: (state) => state.absenceProfiles,
    employeeSchedule: (state) => {
      // find one overlapping with today, or any
      const today = dayjs().valueOf();
      const current = state.employeeSchedules.find(
        (item) => dayjs(item.periodStartDate).valueOf() <= today && today <= dayjs(item.periodEndDate).valueOf(),
      );
      return current ?? state.employeeSchedules[0];
    },
    departmentSchedules: (state) => state.departmentSchedules,
    managerApprovalSchedules: (state) => state.managerApprovalSchedules,
  },

  mutations: {
    SET_PROFILES(state, data: AbsenceProfile[]) {
      state.absenceProfiles = data;
    },
    SET_EMPLOYEE_SCHEDULES(state, data: EmployeeScheduleTaskVariables[]) {
      state.employeeSchedules = data;
    },
    SET_DEPARTMENT_SCHEDULES(state, data: EmployeeScheduleTaskVariables[]) {
      state.departmentSchedules = data;
    },
    SET_MANAGER_APPROVAL_SCHEDULES(state, data: EmployeeScheduleTaskVariables[]) {
      state.managerApprovalSchedules = data;
    },
    SET_SCHEDULER_OPTIONS(state, data: EmployeeScheduleTaskVariables) {
      state.schedulerOptions = data;
    },
    SET_ABSENCE_TYPES(state, data: AbsenceType[]) {
      state.absenceTypes = data;
    },
    SET_ABSENCE_CHART_CASE_VIEW(state, data: api.CaseConfiguration) {
      state.caseView = data;
    },
    SET_ABSENCE_PARSED_ROWS(state, data: any[]) {
      state.schedulerRows = data;
    },
  },

  actions: {
    async loadAbsenceProfiles({ commit }) {
      const profiles = await absenceProfilesApi.getProfiles();
      commit('SET_PROFILES', profiles);
    },
    async loadScheduleTasks({ commit, rootGetters }) {
      const filterByAssignee = rootGetters.user.id;

      // Only fetch variables for schedule-related tasks (filter by formKey upfront)
      const scheduleFormKeys = [
        'scheduleEmployee',
        'scheduleCreateForm',
        'scheduleManagerApproval',
        'scheduleConfirm2Form',
      ];
      const allTasks = await loadTasksWithVariables(filterByAssignee, undefined, scheduleFormKeys);

      // Categorize tasks in a single loop instead of 3 separate filters
      const employeeTasks: EmployeeScheduleTaskVariables[] = [];
      const departmentTasks: EmployeeScheduleTaskVariables[] = [];
      const managerApprovalTasks: EmployeeScheduleTaskVariables[] = [];

      allTasks.forEach((task) => {
        switch (task.task.formKey) {
          case 'scheduleEmployee':
            employeeTasks.push(task);
            break;
          case 'scheduleCreateForm':
            departmentTasks.push(task);
            break;
          case 'scheduleManagerApproval':
          case 'scheduleConfirm2Form':
            managerApprovalTasks.push(task);
            break;
          default:
            break;
        }
      });

      commit('SET_EMPLOYEE_SCHEDULES', employeeTasks);
      commit('SET_DEPARTMENT_SCHEDULES', departmentTasks);
      commit('SET_MANAGER_APPROVAL_SCHEDULES', managerApprovalTasks);
    },
    async setSchedulerOptionsForm({ commit }, item: EmployeeScheduleTaskVariables) {
      commit('SET_SCHEDULER_OPTIONS', item);
    },
    async loadAbsenceTypes({ commit }) {
      const response = await formClient.getForm({ formId: 'absenceForm' });
      const absenceTypes = getAbsenceTypesFromForm(response.model as any);
      commit('SET_ABSENCE_TYPES', absenceTypes);
    },
    async loadSchedule(
      { commit, getters, dispatch },
      opts: GetScheduleRowsResponseRequest & {
        planning?: boolean;
        executionId?: string;
        employeeSelectionAllowed?: (employeeId: string, departmentId: string) => boolean;
      },
    ) {
      const {
        planning,
        executionId,
        employeeSelectionAllowed,
        ...params
      } = opts;
      const scheduleRows = await calendarClient.getScheduleRowsResponse(params);
      if (!getters.absenceChartConfig) {
        await dispatch('loadScheduleConfig');
      }
      const rows = parseRows(
        scheduleRows?.rows,
        getters.absenceChartConfig,
        getters.isSaintGobain,
        planning,
        executionId,
        employeeSelectionAllowed,
      );
      commit('SET_ABSENCE_PARSED_ROWS', rows.rows);
      return { rows, scheduleRows };
    },
    async loadScheduleConfig({ commit }) { // id is meta.caseViewId
      const caseView = await casesviewClient.getCaseView({ id: absencesCaseId });
      // set default colors
      const chartConfig = caseView.meta.chartConfig as ChartConfig;
      if (chartConfig) {
        chartConfig.mappings ??= [];
        chartConfig.mappings.push({
          colorMap: {
            inplanning: '#87b8e982',
            managerInplanning: '#87b8e982',
          },
          field: 'status',
          mapType: 'color',
          legend: [
            {
              values: ['inplanning', 'managerInplanning'],
              title: 'inplanning',
            },
          ],
        });
      }
      commit('SET_ABSENCE_CHART_CASE_VIEW', caseView);
      return caseView;
    },
    async loadValidationIds({ state }) {
      state.employeeValidationIds ??= await getValidationIds('scheduleEmployee');
      state.departmentValidationIds ??= await getValidationIds('scheduleCreateForm');
    },
    async getValidationIds({ state, dispatch }, mode: 'single' | 'schedule' | 'none') {
      await dispatch('loadValidationIds');
      if (mode === 'single') {
        return state.employeeValidationIds?.validationIds ?? {};
      }
      if (mode === 'schedule') {
        return state.departmentValidationIds?.validationIds ?? {};
      }
      return {};
    },
    async getSingleValidationIds({ state, dispatch }, mode: 'single' | 'schedule' | 'none') {
      await dispatch('loadValidationIds');
      if (mode === 'single') {
        return state.employeeValidationIds?.singleValidationIds ?? {};
      }
      if (mode === 'schedule') {
        return state.departmentValidationIds?.singleValidationIds ?? {};
      }
      return {};
    },
  },
});

export default absenceModule;
